import { RAIL_LINK_CLASS, RelatedRail } from "@pej/kore/components";
import { cloneElement, type ReactElement, type ReactNode } from "react";

const NUMBER_FMT = new Intl.NumberFormat("en");

interface CountNavRowProps {
    label: ReactNode;
    /** Child count. `undefined` → em-dash (loading); `0` → muted, non-interactive
     *  row (nothing to navigate to); `n > 0` → the interactive rail row. */
    count: number | undefined;
    /** The element the interactive row BECOMES — a router `<Link>` (detail page)
     *  or a `<button onClick>` (drawer). Cloned with kore's rail-row class plus
     *  the label / count / chevron content. */
    render: ReactElement<{ className?: string }>;
}

// RAIL_LINK_CLASS is applied RAW, never through cn()/tailwind-merge: twMerge isn't
// configured for kore's token scale, so it reads `text-kore-sm` (size) and
// `text-kore-text-muted` (color) as conflicting `text-*` classes and drops the
// size — silently rendering 13px labels at the 16px base. kore's own components
// apply this constant verbatim for the same reason; we follow suit.
function railClass(extra?: string) {
    return extra ? `${RAIL_LINK_CLASS} ${extra}` : RAIL_LINK_CLASS;
}

// One cross-navigation "child count" row, built on kore's RelatedRail row
// affordance (RAIL_LINK_CLASS + RowValue) so the program drawer's Activity
// section, the program detail Activity rail, and the detail right-rails all read
// identically. The count-state logic (loading / empty / linked) lives here, not
// in kore — it's app behaviour, not design-system chrome. A 0 or loading count
// is deliberately non-interactive: there's nothing to navigate to yet.
export function CountNavRow({ label, count, render }: CountNavRowProps) {
    if (count !== undefined && count > 0) {
        return cloneElement(
            render,
            { className: railClass(render.props.className) },
            <span key="label" className="shrink-0">
                {label}
            </span>,
            <RelatedRail.RowValue key="value" numeric>
                {NUMBER_FMT.format(count)}
            </RelatedRail.RowValue>,
        );
    }
    return (
        <div className={railClass("pointer-events-none")}>
            <span className="shrink-0">{label}</span>
            <span className="font-medium tabular-nums text-kore-text-muted">
                {count === undefined ? "—" : NUMBER_FMT.format(count)}
            </span>
        </div>
    );
}
